/**
 * Strict currency parser for staff-entered proposal money.
 * Never use raw parseFloat() on formatted amounts (`parseFloat("10,900") === 10`).
 */

export type ParseProposalCurrencyResult =
  | { ok: true; amount: number }
  | { ok: false; reason: string };

const CURRENCY_RE = /^\s*(?:S\$|\$)?\s*(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,2}))?\s*$/;

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Parse a full-string currency amount.
 * Accepts: 10900, 10,900, 10,900.00, $10,900, S$10,900, surrounding spaces.
 * Rejects: blank, malformed, negative, NaN/Infinity, partial parses.
 */
export function parseProposalCurrencyAmount(
  raw: unknown,
  options?: { allowZero?: boolean },
): ParseProposalCurrencyResult {
  const allowZero = options?.allowZero === true;

  if (typeof raw === "number") {
    if (!Number.isFinite(raw)) return { ok: false, reason: "Amount is not a finite number." };
    if (raw < 0) return { ok: false, reason: "Amount cannot be negative." };
    if (raw === 0 && !allowZero) return { ok: false, reason: "Amount must be greater than zero." };
    return { ok: true, amount: roundMoney(raw) };
  }

  if (typeof raw !== "string") {
    return { ok: false, reason: "Amount must be a number or currency string." };
  }

  const trimmed = raw.trim();
  if (!trimmed) return { ok: false, reason: "Amount is required." };

  const match = CURRENCY_RE.exec(trimmed);
  if (!match) {
    return { ok: false, reason: "Amount is not a valid currency value." };
  }

  const whole = match[1]!.replace(/,/g, "");
  const cents = match[2] ?? "00";
  const n = Number(`${whole}.${cents}`);
  if (!Number.isFinite(n)) return { ok: false, reason: "Amount is not a finite number." };
  if (n < 0) return { ok: false, reason: "Amount cannot be negative." };
  if (n === 0 && !allowZero) return { ok: false, reason: "Amount must be greater than zero." };
  return { ok: true, amount: roundMoney(n) };
}

export function parsePositiveProposalCurrencyAmount(raw: unknown): ParseProposalCurrencyResult {
  return parseProposalCurrencyAmount(raw, { allowZero: false });
}
